import React, { useState } from 'react'
import { AssetDetail, AssetFile } from '@/modules/Asset/types'

import { randomAvatarSrc, formatDateWithFormatter } from '@/utils/function'
import { Avatar, Typogaphy } from 'vnetwork-ui'
import { Badge } from '@/components/ui/badge'
import AssetStatusBadge from '@/modules/Asset/components/AssetStatusBadge'
import { Calendar, FileText, FolderOpen, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAssetFileAccess } from '@/modules/Asset/hooks/useAssetFileAccess'
import ErrorBoundary from '@/components/ErrorBoundary'

interface AssetCardProps {
    asset: AssetDetail
    onClick?: () => void
}

const LocalAttachmentCard: React.FC<{
    file: AssetFile
    canDownload: boolean
}> = ({ file, canDownload }) => {
    const { openAssetFile } = useAssetFileAccess()

    const attachment = {
        attachment_file_name: file.file_name || 'unknown',
        attachment_file_size: file.file_size ?? 0,
    }

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    }

    const isPDF = attachment.attachment_file_name
        ?.toLowerCase()
        .endsWith('.pdf')

    return (
        <ErrorBoundary>
            <div
                className="flex items-center gap-3 p-2 bg-gray-50 border border-gray-100 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
                onClick={(e) => {
                    e.stopPropagation()
                    openAssetFile(file, canDownload)
                }}
            >
                <div className="flex-shrink-0">
                    <div
                        className={`w-8 h-8 ${isPDF ? 'bg-red-50 text-red-500' : 'bg-blue-50 text-blue-500'} rounded flex items-center justify-center`}
                    >
                        <FileText className="w-4 h-4" />
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <Typogaphy className="font-medium text-xs text-gray-700 truncate">
                        {attachment.attachment_file_name}
                    </Typogaphy>
                    <div className="text-xs text-gray-400">
                        {formatFileSize(attachment.attachment_file_size)}
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    )
}

const AssetCard: React.FC<AssetCardProps> = ({ asset, onClick }) => {
    const { t } = useTranslation('dashboard')
    const [isExpanded, setIsExpanded] = useState(false)

    const toggleExpand = (e: React.MouseEvent) => {
        e.stopPropagation()
        setIsExpanded(!isExpanded)
    }

    return (
        <div
            className="group flex flex-col gap-2.5 p-4 bg-white border border-gray-200/80 rounded-xl hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer"
            onClick={onClick}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-base text-gray-900 truncate group-hover:text-primary transition-colors">
                            {asset.asset_name}
                        </h3>
                        {asset.asset_slug && (
                            <Badge
                                variant="outline"
                                className="text-xs py-0 px-1.5 h-auto font-normal text-gray-500 border-gray-200 flex-shrink-0"
                            >
                                {asset.asset_slug}
                            </Badge>
                        )}
                    </div>

                    <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                        <div className="flex items-center gap-1.5">
                            <FolderOpen className="w-3.5 h-3.5" />
                            <span>{asset.department_name}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5" />
                            <span>
                                {t(
                                    `asset:document_types.${asset.current_version?.document_type}`
                                ) || asset.current_version?.document_type}
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>
                                {formatDateWithFormatter(
                                    asset.updated_at,
                                    'DD/MM/YYYY'
                                )}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    {asset.current_version?.asset_version_status && (
                        <AssetStatusBadge
                            status={asset.current_version.asset_version_status}
                            className="text-xs py-0.5 px-2 h-auto"
                        />
                    )}
                    {asset.current_version?.version_code && (
                        <Badge
                            variant="outline"
                            className="text-xs py-0 px-1.5 h-auto font-mono"
                        >
                            {asset.current_version.version_code}
                        </Badge>
                    )}
                </div>
            </div>

            {asset.asset_description && (
                <Typogaphy className="text-xs text-gray-500 line-clamp-2 border-l-2 border-gray-200 pl-3 py-0.5">
                    {asset.asset_description}
                </Typogaphy>
            )}

            <div className="flex items-center justify-between gap-4 pt-1">
                <div className="flex items-center gap-2">
                    <Avatar
                        src={randomAvatarSrc(asset.created_by_name || 'U')}
                        className="!w-6 !h-6"
                    />
                    <span className="text-xs text-gray-500">
                        {asset.created_by_name}
                    </span>
                </div>
                <div
                    className="flex items-center gap-1.5 text-xs font-semibold text-primary/70 hover:text-primary transition-all cursor-pointer select-none group/toggle px-2 py-1 rounded-md hover:bg-primary-lighter/30"
                    onClick={toggleExpand}
                >
                    <FileText className="w-3.5 h-3.5" />
                    <span>
                        {isExpanded
                            ? t('common.hide_details')
                            : `${t('common.show_files')} (${asset.files?.length || 0})`}
                    </span>
                    <ChevronRight
                        className={`w-3.5 h-3.5 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''} group-hover/toggle:translate-x-0.5`}
                    />
                </div>
            </div>

            {isExpanded && asset.files?.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1 animate-in slide-in-from-top-2 duration-200">
                    {asset.files.map((file, i) => (
                        <LocalAttachmentCard
                            key={i}
                            file={file}
                            canDownload={asset.can_download !== false}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

export default AssetCard
